import React from 'react';
import { motion } from 'framer-motion';
import { Github, ArrowUpRight } from 'lucide-react';
import Card from './Card';
import Badge from './Badge';
import { Project } from '../shared/data';

interface ProjectsProps {
  projects: Project[];
}

const ProjectCard: React.FC<Project & { index: number }> = ({
  title,
  description,
  tech,
  github,
  liveUrl,
  year,
  index,
}) => (
  <motion.div
    className="pb-10 last:pb-0 border-b last:border-b-0 border-neutral-200 dark:border-neutral-800"
    initial={{ opacity: 0, y: 6 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.3, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="mb-2">
      {year && (
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-500 mb-1">
          {year}
        </div>
      )}
      <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50">
        {title}
      </h3>
    </div>
    <p className="text-[15px] leading-[1.6] text-neutral-600 dark:text-neutral-400 max-w-2xl">
      {description}
    </p>
    {tech.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-4">
        {tech.map((t, i) => (
          <Badge key={i}>{t}</Badge>
        ))}
      </div>
    )}
    {(github || liveUrl) && (
      <div className="flex flex-wrap gap-2 mt-5">
        {github && (
          <a
            href={github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-300 dark:border-neutral-700 text-neutral-800 dark:text-neutral-200 font-mono text-[11px] uppercase tracking-[0.14em] hover:border-neutral-900 dark:hover:border-neutral-300 transition-colors"
          >
            <Github size={12} />
            Github
            <ArrowUpRight size={12} />
          </a>
        )}
        {liveUrl && (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent hover:bg-accent-hover text-neutral-950 font-mono text-[11px] uppercase tracking-[0.14em] font-medium transition-colors"
          >
            Live
            <ArrowUpRight size={12} />
          </a>
        )}
      </div>
    )}
  </motion.div>
);

const Projects: React.FC<ProjectsProps> = ({ projects }) => {
  if (projects.length === 0) return null;

  return (
    <div id="projects">
      <Card title="Projects" index={3}>
        <div className="space-y-10">
          {projects.map((p, index) => (
            <ProjectCard key={index} index={index} {...p} />
          ))}
        </div>
      </Card>
    </div>
  );
};

export default Projects;
